import { Alert } from '@ksyos/design-system';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Async from 'react-select/async';
import type { IReduxState } from '../../../../../configureStore';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IProcess } from '../../../../interfaces/process';
import type { IReactSelectOptions } from '../../../../interfaces/reactSelectOptions';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { UrlBuilder } from '../../../../../../common/utils/urlBuilder';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { Feedback } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { SelectOption } from '../../../../elements/SelectOption';

interface ISelectedConsumers {
    selectedDiagnosticReportConsumerPractitionerId?: string | null;
}

interface ICareProviderAndRequesterAndSelectedConsumers extends ISelectedConsumers {
    draftCareProviderOrganizationId: string | null;
    requesterOrganizationId: string | null;
}

interface IPractitioner {
    id: string;
    isActivated: boolean;
    nameFamily?: string;
    nameGiven?: string;
    nameInfix?: string;
}

interface IPractitionerOptions {
    label: string;
    value: string;
}

interface ICounselorSelectorProps {
    careProviderAndRequesterAndSelectedConsumers: ICareProviderAndRequesterAndSelectedConsumers;
    errors: ValidationErrors;
    handleChange: <K extends keyof ISelectedConsumers>(name: K, value: ISelectedConsumers[K]) => Promise<void>;
    procedurePerformerOrganizationId: string;
    process: IProcess | undefined;
    service: IServiceGetOne;
    validateOne: (name: keyof ISelectedConsumers) => Promise<void>;
}

export function CounselorSelector(props: ICounselorSelectorProps) {
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    const [practitioners, setPractitioners] = useState<IPractitionerOptions[] | undefined>();
    const [selectedOption, setSelectedOption] = useState<IReactSelectOptions>();
    const [noServiceConsumersOnMount, setNoServiceConsumersOnMount] = useState(false);

    useEffect(() => {
        loadPractitioners(props.service.id, true);
    }, []);

    return (
        <>
            {props.service.code === ServiceCode.GGZBther && (
                <>
                    <HorizontalFormGroup hasErrors={props.errors.has('selectedDiagnosticReportConsumerPractitionerId')}>
                        <FormLabel isRequired>{translateKey('request.selectCounselor')}</FormLabel>

                        <InputContainer>
                            {practitioners && !noServiceConsumersOnMount && (
                                <>
                                    <Async<IReactSelectOptions>
                                        className={classnames('select-form-control', 'automation-counselorSelector')}
                                        classNamePrefix="Select"
                                        components={{ Option: SelectOption }}
                                        defaultOptions
                                        key={`${CounselorSelector}-reload`} // refreshToken will be added in a newer version, does not exist in 1.7.12
                                        loadOptions={async (input: string) => getOptions(input)}
                                        noOptionsMessage={() => translateKey('common.typeToSearch')}
                                        onChange={async (newSelectedOption) => {
                                            if (newSelectedOption && !Array.isArray(newSelectedOption)) {
                                                await handleChange(newSelectedOption);
                                            }
                                        }}
                                        placeholder={translateKey('request.selectCounselor')}
                                        value={selectedOption}
                                    />
                                    <Feedback
                                        error={props.errors.get('selectedDiagnosticReportConsumerPractitionerId')}
                                    />
                                </>
                            )}
                        </InputContainer>
                    </HorizontalFormGroup>

                    {noServiceConsumersOnMount && props.service.code && (
                        <Alert appearance="warning">{translateKey('request.noProvidersForRequest')}</Alert>
                    )}
                </>
            )}
        </>
    );

    async function handleChange(newSelectedOption?: IReactSelectOptions) {
        setSelectedOption(newSelectedOption);

        const selectedPractitionerId = newSelectedOption?.value || null;

        await onChange(selectedPractitionerId);
        if (
            selectedPractitionerId !==
            props.careProviderAndRequesterAndSelectedConsumers.selectedDiagnosticReportConsumerPractitionerId
        ) {
            await props.validateOne('selectedDiagnosticReportConsumerPractitionerId');
        }
    }

    async function getOptions(input: string) {
        await loadPractitioners(props.service.id, false, input);
        const options: { className?: string; label: string; value: string }[] = [];
        if (practitioners) {
            practitioners.map((practitioner) => {
                const classNamePractitioner = 'Select__group-option';
                options.push({
                    value: practitioner.value,
                    label: practitioner.label,
                    className: classNamePractitioner,
                });
            });
        }

        return options;
    }

    async function onChange(selectedPractitionerId: string | null) {
        await props.handleChange('selectedDiagnosticReportConsumerPractitionerId', selectedPractitionerId);
    }

    async function loadPractitioners(serviceId: string, initialMount: boolean, searchInput?: string) {
        const urlBuilder = new UrlBuilder('/api/serviceConsumers');
        urlBuilder.add('organizationId', organizationId);
        urlBuilder.add('serviceId', serviceId);
        urlBuilder.add('isActive', true);

        if (searchInput) {
            urlBuilder.add('searchPractitioner', searchInput);
        }

        const url = urlBuilder.build();
        const result = await fetchJson(url, 'GET');

        const practitionerOptions: { label: string; value: string }[] = [];
        for (const serviceConsumer of result.json.items) {
            practitionerOptions.push({
                label: createPractitionerLabel(serviceConsumer.employment.practitioner),
                value: serviceConsumer.employment.practitioner.id,
            });
        }

        setPractitioners(practitionerOptions);

        if (initialMount) {
            await initializeSelectedOption(practitionerOptions);
            if (practitionerOptions.length === 0) {
                setNoServiceConsumersOnMount(true);
            }
        }
    }

    async function initializeSelectedOption(practitionersOptions: IPractitionerOptions[]) {
        let initializePractitionerId =
            (props.process && props.process.selectedDiagnosticReportProviderPractitionerId) ||
            props.careProviderAndRequesterAndSelectedConsumers.selectedDiagnosticReportConsumerPractitionerId;

        if (!initializePractitionerId) {
            initializePractitionerId = practitionerId;
        }

        if (practitionersOptions.length === 1) {
            initializePractitionerId = practitionersOptions[0].value;
        }

        if (practitionersOptions.length > 0) {
            const value = initializePractitionerId;
            const initialSelectedOption: { label: string; value: string } | undefined = practitionersOptions.find(
                (item) => item.value === value
            );

            if (initialSelectedOption) {
                setSelectedOption(initialSelectedOption);
                await onChange(initializePractitionerId);
                await props.validateOne('selectedDiagnosticReportConsumerPractitionerId');
            } else {
                await onChange(null);
            }
        } else {
            await onChange(null);
        }
    }

    function createPractitionerLabel(practitioner: IPractitioner) {
        const label = getDisplayName(practitioner);

        return label;
    }
}
